import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Skeleton,
  TableCell,
  TableHead,
  TableRow
} from "@mui/material";
import React, { useContext, useRef, useState } from "react";

import ErrorView from "../../../Component/ErrorView/ErrorView";
import MUIIconButton from "../../../Component/UI/MUIIconButton/MUIIconButton";
import { extractDomain } from "../../../utils/helpers";
import { DiscoveryContext } from "../discovery-context";
import useGenerateProductTaxonomies from "../useGenerateProductTaxonomies";
import MUICard from "./../../../Component/UI/MUICard/MUICard";
import MUIToolTip from "./../../../Component/UI/MUIToolTip/MUIToolTip";
import MUITable, {
  StyledTableCell,
  StyledTableRow,
} from "./../../../Component/UI/Table/Table";
import classes from "./Taxonomy.module.css";

const TABLE_HEADERS = [
  {
    id: "taxonomy",
    name: "Product Dimensions",
  },
];

function Row({ category }) {
  const [open, setOpen] = useState(false);
  const discoveryCtx = useContext(DiscoveryContext);

  return (
    <React.Fragment key={category.name}>
      <StyledTableRow>
        <StyledTableCell className={classes.attribute_headings}>
          <div className={classes.heading_wrapper}>
            <MUIIconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </MUIIconButton>
            <span className={classes.heading_text}>{category.name}</span>
          </div>
        </StyledTableCell>
        {category?.products?.map((product) => {
          let color = "#dc3545";
          if (product.score >= 7) color = "#2196F3";
          if (product.score >= 9) color = "#198754";

          return (
            <StyledTableCell
              style={{ color }}
              className={classes.attribute_score}
              key={product.name}
            >
              {product.score}
            </StyledTableCell>
          );
        })}
      </StyledTableRow>
      {open &&
        category?.products?.[0]?.subcategories?.map((sub, idx) => (
          <StyledTableRow key={idx} className={classes.sub_row}>
            <StyledTableCell className={classes.attribute_sub_headings}>
              {sub.name}
            </StyledTableCell>
            {category.products.map((product, jdx) => {
              let color = "#dc3545";
              let rating = "low";
              const item = product?.subcategories?.[idx];

              if (item?.score >= 7) {
                rating = "fair";
                color = "#2196F3";
              }
              if (item?.score >= 9) {
                rating = "high";
                color = "#198754";
              }

              let sources = [];
              if (item?.sources) {
                sources = item?.sources?.map((source) => ({
                  source: source,
                  snippet: "",
                }));
              }

              return (
                <MUIToolTip
                  key={jdx}
                  llm={discoveryCtx.llm}
                  additionalText={`${product.name} received a rating of ${item?.score} out of 10, a ${rating} score in ${sub.name}`}
                  title={item.explanation}
                  arrow
                  disableAction
                  showSources
                  actionText="Add To Actions"
                  llmSources={sources}
                  slotProps={{
                    popper: {
                      modifiers: [
                        {
                          name: "offset",
                          options: {
                            offset: [0, -18],
                          },
                        },
                      ],
                    },
                  }}
                >
                  <StyledTableCell
                    style={{ color }}
                    className={classes.attribute_score}
                  >
                    {product?.subcategories?.[idx]?.score}
                  </StyledTableCell>
                </MUIToolTip>
              );
            })}
          </StyledTableRow>
        ))}
    </React.Fragment>
  );
}

export default function Taxonomy({ category, llm }) {
  const [error, setError] = useState(null);
  const discoveryCtx = useContext(DiscoveryContext);
  const { custom_taxonomies } = useGenerateProductTaxonomies();
  console.log('custom_taxonomies', custom_taxonomies)


  function LoadingSkeletion() {
    return (
      <div className="p-3">
        <Skeleton variant="rectangular" height={30} />
        {Array(10)
          .fill(0)
          .map((_, index) => (
            <Skeleton
              variant="rectangular"
              width={index % 2 === 0 ? "70%" : "97%"}
              key={index}
              className="my-3"
              height={20}
            />
          ))}
      </div>
    );
  }


  function renderHeaders() {
    const headers = [
      ...TABLE_HEADERS,
      ...discoveryCtx.competitors['default'].map((item) => ({
        id: item.name,
        name: item.name,
      })),
      ...discoveryCtx.competitors[category].map((item) => ({
        id: item.name,
        name: item.name,
      })),
    ];

    return (
      <TableHead className={classes.table_head}>
        <TableRow>
          {headers.map((header, index) => {
            let ctxCompetitor = null;
            if (index == 1) {
              ctxCompetitor = discoveryCtx.competitors['default'][0];
            }
            if (index > 1) {
              ctxCompetitor = discoveryCtx.competitors[category][index - 2];
            }

            let domain = "";
            if (ctxCompetitor && ctxCompetitor?.domain) {
              domain = extractDomain(ctxCompetitor?.domain);
            }

            return (
              <TableCell key={index} className={classes.table_head_cell}>
                <div className={classes.table_head_content}>
                  {index != 0 && (
                    <img
                      data-html2canvas-ignore
                      className={classes.table_head_logo}
                      src={`https://img.logo.dev/${domain}?token=pk_SaDf4t2eRsW5Fc84zgJRZA`}
                    />
                  )}
                  <div
                    className={
                      index === 0
                        ? classes.table_head_text_comp
                        : classes.table_head_text
                    }
                  >
                    {header.name}
                  </div>
                </div>
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
    );
  }

  function roundNumber(num) {
    return num % 1 < 0.5 ? Math.floor(num) : Math.ceil(num);
  }

  function renderRows(category) {
    return <Row key={category.name} category={category} />;
  }

  let loading = true;
  let categories = [];
  let sortedCategories = [];

  if (
    discoveryCtx?.taxonomies &&
    Object.keys(discoveryCtx?.taxonomies?.[llm]?.[category] || {}).length > 0
  ) {
    categories = discoveryCtx?.taxonomies?.[llm]?.[category]?.categories.map((categoryRecord) => ({
      name: categoryRecord.name,
      products: discoveryCtx?.taxonomies?.[llm]?.[category]?.products.map((product) => {
        const subcategories = Object.values(
          product.scores[categoryRecord.name]?.subcategories || {}
        );
        const totalScore = subcategories.length
          ? subcategories.reduce(
            (acc, item) => acc + (isNaN(+item.score) ? 0 : +item.score),
            0
          )
          : 0;
        const average = subcategories.length
          ? totalScore / subcategories.length
          : 0;

        return {
          name: product.name,
          score: roundNumber(average),
          subcategories:
            Object.keys(
              product.scores[categoryRecord.name]?.subcategories || {}
            )?.map((subcat) => ({
              name: subcat,
              sources:
                product.scores[categoryRecord.name]?.subcategories?.[subcat].sources,
              score:
                product.scores[categoryRecord.name]?.subcategories?.[subcat].score,
              explanation:
                product.scores[categoryRecord.name]?.subcategories?.[subcat]
                  .explanation,
            })) || [],
        };
      }),
    }));

    const headerOrder = [
      ...discoveryCtx.competitors['default'].map(item => item.name.trim()),
      ...discoveryCtx.competitors[category].map(item => item.name.trim())
    ];

    sortedCategories = categories.map(cat => ({
      ...cat,
      products: [...cat.products].sort(
        (a, b) => headerOrder.indexOf(a.name.trim()) - headerOrder.indexOf(b.name.trim())
      )
    }));

    loading = false;
  }
  return (
    <MUICard variant="elevated" elevation={0} className={classes.card}>
      {!loading && !error && categories?.length > 0 && (
        <MUITable
          elevation={0}
          data={sortedCategories}
          renderHeaders={() => renderHeaders()}
          render={renderRows}
        />
      )}
      {error && (
        <div className={classes.error}>
          <ErrorView
            title={"Error fetching category dimensions"}
            message={error}
            retry
            onRetry={() => { }}
          />
        </div>
      )}
      {loading && !error && <LoadingSkeletion />}
    </MUICard>
  );
}
